/**
 * Design Tokens - Centralized Design System
 * Based on UX Design Token Screenshots (56-60)
 *
 * These tokens ensure consistency across the entire application
 * and make theme customization easier.
 */

import { purpleColors, purpleColorsDark, statusColors, elevationNeutrals } from './colors';

/**
 * Size Tokens
 * Based on Screenshot 59 - Size tokens
 *
 * Used for component heights, widths, and icon sizes
 */
export const sizeTokens = {
  sizeXXS: 8,   // Extra extra small
  sizeXS: 12,   // Extra small
  sizeSM: 16,   // Small
  size: 20,     // Default size
  sizeMS: 22,   // Medium-small (between default and MD)
  sizeMD: 24,   // Medium
  sizeLG: 28,   // Large
  sizeXL: 36,   // Extra large
  sizeXXL: 52,  // Extra extra large
};

/**
 * Control Height Tokens
 * Standardized heights for interactive controls (buttons, inputs, selects)
 *
 * These ensure consistent sizing across all form controls and action buttons.
 */
export const controlHeightTokens = {
  controlHeightSM: 32,   // Small controls (compact forms)
  controlHeight: 36,     // Default control height
  controlHeightLG: 40,   // Large controls (prominent actions)
  controlHeightXL: 44,   // Extra large controls (primary toolbars, hero sections)
  controlHeightXXL: 48,  // Extra extra large (special emphasis)
};

/**
 * Spacing Tokens - Margin
 * Based on Screenshot 60 - Space tokens
 *
 * Used for margins between components
 */
export const marginTokens = {
  marginXXS: 4,   // Extra extra small margin
  marginXS: 8,    // Extra small margin
  marginSM: 12,   // Small margin
  margin: 16,     // Default margin
  marginMD: 20,   // Medium margin
  marginLG: 24,   // Large margin
  marginXL: 32,   // Extra large margin
  marginXXL: 48,  // Extra extra large margin
};

/**
 * Spacing Tokens - Padding
 * Based on Screenshot 60 - Space tokens
 *
 * Used for internal spacing within components
 */
export const paddingTokens = {
  paddingXXS: 4,   // Extra extra small padding
  paddingXS: 8,    // Extra small padding
  paddingSM: 12,   // Small padding
  padding: 16,     // Default padding
  paddingMD: 20,   // Medium padding
  paddingLG: 24,   // Large padding
  paddingXL: 32,   // Extra large padding
  paddingXXL: 48,  // Extra extra large padding
};

/**
 * Typography Tokens
 * Based on Screenshot 58 - Text Styles
 *
 * Exact pixel values and line heights from design system
 */
export const typographyTokens = {
  // Heading styles
  h1: {
    size: 38,
    lineHeight: 46,
    weight: 'bold' as const,
  },
  h2: {
    size: 30,
    lineHeight: 38,
    weight: 'bold' as const,
  },
  h3: {
    size: 24,
    lineHeight: 32,
    weight: 'bold' as const,
  },
  h4: {
    size: 20,
    lineHeight: 28,
    weight: 'bold' as const,
  },
  h5: {
    size: 16,
    lineHeight: 24,
    weight: 'bold' as const,
  },

  // Body text styles
  base: {
    size: 16,
    lineHeight: 24,
    weight: 'normal' as const,
  },
  sm: {
    size: 14,
    lineHeight: 22,
    weight: 'normal' as const,
  },
  lg: {
    size: 18,
    lineHeight: 28,
    weight: 'normal' as const,
  },

  // Text modifiers
  strong: {
    weight: 'bold' as const,
  },
  italic: {
    style: 'italic' as const,
  },

  // Table-specific typography
  table: {
    headerFontSize: 14,      // Table header text
    cellFontSize: 14,        // Table cell text
    headerFontWeight: 600,   // Semibold for headers
    cellFontWeight: 400,     // Normal for cells
  },
};

/**
 * Border Radius — the canonical vocabulary is now the semantic `radiusTokens`
 * concentric scale defined just below. The legacy `borderRadiusTokens` names live
 * on as a thin re-export ALIAS (declared right after `innerRadius`) so every
 * existing call site keeps resolving — now to the semantic values, not its own
 * literal ladder.
 */

/**
 * Concentric Radius Scale (semantic rungs — the canonical radius vocabulary)
 *
 * Only the four rungs actually consumed by the category-experience waves are
 * authored here (chip / control / card / panel). Further rungs (e.g. `overlay: 16`)
 * are deferred to the carrier-first shared-token rollout that owns the full ladder.
 *
 *   chip    4  — level chips / pills nested inside a control
 *   control 6  — flat-chrome interactive controls (inputs, selects, buttons)
 *   card    8  — inner fields + detail / admin cards
 *   panel  12  — the quick-create popover / elevated panel
 *
 * Squircle note: Figma's 60% corner-smoothing ≈ an iOS superellipse. CSS has no
 * true superellipse primitive (would need SVG/clip-path), so these plain radii
 * approximate it. True continuous-corner rendering is a documented Figma-side
 * note only — intentionally out of this build.
 */
export const radiusTokens = {
  chip: 4,
  control: 6,
  card: 8,
  panel: 12,
} as const;

/**
 * Concentric radius rule: a rounded element nested inside a rounded, padded
 * container should use `inner = outer − padding` so the corners stay concentric.
 * Clamped to `radiusTokens.chip` (4) so a tight/negative result never squares off.
 *
 *   innerRadius(12, 8) === 4   // panel padded by 8 → card-ish inner
 *   innerRadius(6, 8)  === 4   // clamped (6 − 8 = −2 → floor at chip)
 */
export const innerRadius = (outer: number, padding: number): number =>
  Math.max(outer - padding, radiusTokens.chip);

/**
 * Border Radius Tokens (BACK-COMPAT ALIAS → points at the semantic scale)
 *
 * Historically its own literal ladder; as of the holistic radius migration it is a
 * thin re-export of `radiusTokens` at the EQUIVALENT rung, so every existing
 * consumer (`theme.ts`'s `borderRadiusXS`, `HighlightText`, `cardStyleTokens`, and
 * any surface importing `borderRadiusTokens.*`) resolves to the canonical values
 * without a churny per-site edit. New work should import `radiusTokens` directly.
 *
 *   borderRadiusXS  2  → kept verbatim (AntD arrow radius — no semantic equivalent)
 *   borderRadiusSM  6  → radiusTokens.control (6)
 *   borderRadius    8  → radiusTokens.card    (8)
 *   borderRadiusLG 11  → radiusTokens.panel   (12) — deliberate +1px, user-approved
 */
export const borderRadiusTokens = {
  borderRadiusXS: 2,                     // Extra small — AntD arrow radius (kept)
  borderRadiusSM: radiusTokens.control,  // 6  — small buttons, inputs
  borderRadius: radiusTokens.card,       // 8  — default cards, buttons, inputs
  borderRadiusLG: radiusTokens.panel,    // 12 — panels, modals (was 11)
};

/**
 * Shadow Tokens
 * For depth and elevation
 */
export const shadowTokens = {
  boxShadowSM: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
  boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
  boxShadowMD: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
  boxShadowLG: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
  boxShadowXL: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
};

/**
 * Dark mode shadows (lighter for visibility)
 */
export const shadowTokensDark = {
  boxShadowSM: '0 1px 2px 0 rgba(0, 0, 0, 0.2)',
  boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.3)',
  boxShadowMD: '0 4px 6px -1px rgba(0, 0, 0, 0.3), 0 2px 4px -1px rgba(0, 0, 0, 0.2)',
  boxShadowLG: '0 10px 15px -3px rgba(0, 0, 0, 0.3), 0 4px 6px -2px rgba(0, 0, 0, 0.2)',
  boxShadowXL: '0 20px 25px -5px rgba(0, 0, 0, 0.3), 0 10px 10px -5px rgba(0, 0, 0, 0.2)',
};

/**
 * Elevation Tokens — the shadow-border ("de-border") elevation set
 *
 * A surface reads as elevated via a layered soft SHADOW instead of a hard 1px
 * border, plus a WCAG-compliant hairline RING so the boundary is still
 * perceivable for non-text-contrast (WCAG 1.4.11) and against a printed/flat
 * rendering. Consumed by `useElevation()` (Slice 2.2) and applied per-surface
 * inside the slices that already edit those surfaces (quick-create / tree /
 * admin panel / detail cards) — never as a standalone "apply tokens" pass.
 *
 * Per mode:
 *   ring        1px hairline COLOR that INDEPENDENTLY clears WCAG 1.4.11 3:1 vs
 *               the mode's `colorBgContainer`/`colorBgElevated`. This is the
 *               compliance element (see `elevationNeutrals` in colors.ts for the
 *               measured ratios). It is a NEW token — the existing #d9d9d9 border
 *               is only ~1.41:1 and FAILS.
 *   ringSubtle  decorative-only separator (1.4.11 decoration exemption) for edges
 *               where an adjacent shadow already carries the boundary. NOT gated.
 *   shadow      layered soft de-border recipe — the tight control-top shadow
 *               (boxShadowSM) stacked under the medium spread (boxShadowMD).
 */
export const elevationTokens = {
  light: {
    ring: elevationNeutrals.light.ring,
    ringSubtle: elevationNeutrals.light.ringSubtle,
    shadow: `${shadowTokens.boxShadowSM}, ${shadowTokens.boxShadowMD}`,
  },
  dark: {
    ring: elevationNeutrals.dark.ring,
    ringSubtle: elevationNeutrals.dark.ringSubtle,
    shadow: `${shadowTokensDark.boxShadowSM}, ${shadowTokensDark.boxShadowMD}`,
  },
} as const;

/**
 * Focus Ring Tokens — keyboard-focus indicator, derived from the purple primary
 * (`colorPrimary`). `useElevation()` resolves the live `token.colorPrimary` at
 * runtime for light/dark parity; this default color is the light-mode primary.
 * `width`/`offset` are the fixed geometry of the ring.
 */
export const focusRingTokens = {
  color: purpleColors.purple6, // colorPrimary (#7257F8 light); dark resolves to #9254DE at runtime
  width: 2,
  offset: 2,
} as const;

/**
 * Table Height Tokens
 * Standardized row heights for tables
 */
export const tableHeightTokens = {
  rowHeightCompact: 32,      // Compact density (max data)
  rowHeightStandard: 40,     // Standard density (balanced)
  rowHeightComfortable: 48,  // Comfortable density (spacious)
};

/**
 * Card Styling Tokens
 * Standardized card/section styling for consistent appearance
 */
export const cardStyleTokens = {
  // Subtle elevation (recommended for white sections)
  subtle: {
    borderRadius: radiusTokens.panel,  // 12px (panel rung — was 11)
    padding: paddingTokens.padding,     // 16px
    // Shadow and border applied via theme tokens at runtime
  },
  // Border only (flat design)
  flat: {
    borderRadius: radiusTokens.panel,  // 12px (panel rung — was 11)
    padding: paddingTokens.padding,
  },
  // Elevated (stronger shadow)
  elevated: {
    borderRadius: radiusTokens.panel,  // 12px (panel rung — was 11)
    padding: paddingTokens.padding,
  },
};

/**
 * Color Tokens
 * Reference to color system
 */
export const colorTokens = {
  // Primary brand color - PURPLE
  primary: purpleColors.purple6,

  // Info color - Maps to purple (not blue!)
  info: purpleColors.purple6,

  // Link color - Maps to purple (not blue!)
  link: purpleColors.purple6,

  // Status colors (unchanged)
  success: statusColors.success,
  warning: statusColors.warning,
  error: statusColors.error,
};

/**
 * Complete Design Tokens
 * Export all tokens as a single object
 */
export const designTokens = {
  // Colors
  colors: colorTokens,
  purple: purpleColors,
  purpleDark: purpleColorsDark,
  status: statusColors,

  // Sizing
  size: sizeTokens,
  controlHeight: controlHeightTokens,

  // Spacing
  margin: marginTokens,
  padding: paddingTokens,

  // Typography
  typography: typographyTokens,

  // Tables
  tableHeight: tableHeightTokens,

  // Cards
  cardStyle: cardStyleTokens,

  // Border radius
  borderRadius: borderRadiusTokens,   // back-compat alias
  radius: radiusTokens,               // semantic concentric scale
  innerRadius,                        // concentric-corner helper

  // Shadows
  shadow: shadowTokens,
  shadowDark: shadowTokensDark,

  // Elevation (shadow-border set + focus ring)
  elevation: elevationTokens,
  focusRing: focusRingTokens,
};

export default designTokens;
